import numbro from "numbro";
import { asNumberString } from "../../utils/formatters";

/**
 * Function to generate html template for 'lineToolTipFormatter'
 * @param {Object} param0 Object with title and rows
 */
const tooltipTemplate = ({ title, rows }) => {
  let stringifiedRows = "";

  rows.forEach(
    (row) =>
    (stringifiedRows += `
      <div style="display: flex; align-items: center;">
        <span style="width: 10px;height: 10px;background: ${row.color};border-radius: 50%; margin-right: 5px;"></span>
        <span>${row.label}:&nbsp;<b>${row.value}</b></span>
      </div>
    `)
  );

  return `
      <div class='chart-tooltip'>
        <span><b>${title}</b></span></br>
        ${stringifiedRows}
      </div>
    `;
};

/**
 *
 * Group each legend item on the x axis
 *
 * @param {Array} rows Array of arrays for google chart to draw a row
 * @param {Array} columns Array of arrays for google chart to draw a column
 * @param {Array} legendColors Array of objects with colors for each legend
 */
export const lineToolTipFormatter = (rows, columns, legendColors) => {
  if (!Array.isArray(rows) || !Array.isArray(columns)) {
    return;
  }
  const legends = columns.filter((col) => col.role === "data").map((col) => col.label);
  const legendsByColor = legendColors.map((l) => l.color);

  for (const row of rows) {
    // Row label on the x-axis
    const rowItemWithTitle = row.find((rowItem) => rowItem.type === "string");

    if (rowItemWithTitle) {
      const groupTitle = rowItemWithTitle.v; // like -> Aug, 2020
      const newRowList = row
        .filter((rowItem) => rowItem.type === "number") // Get all values
        .map((rowItem, rowIndex) => ({
          // Create a new object for each
          label: legends[rowIndex],
          color: legendsByColor[rowIndex],
          value: asNumberString(rowItem.v)
        }))
        .sort((a, b) => b.value - a.value); // Sort desc by values

      // Create tooltip with all the data for the row
      const tooltip = tooltipTemplate({
        title: groupTitle,
        rows: newRowList
      });

      // For each item in the row replace the current tooltip with the grouped tooltip
      for (const rowItem of row) {
        if (rowItem.p && rowItem.p.html) {
          rowItem.v = tooltip;
        }
      }
    }
  }

  return {
    rows,
    columns
  };
};

/**
 *
 * Get table height depending on the rows from the server
 *
 * @param {Array} _rows Array of rows passed
 * @param {Array} data Array of both rows and columns
 * @param {Number} height Dynamic height to set table
 */
export const getTableHeight = (_rows, data, height) => {
  // data can have a combination of table header with rows. We want to use either data or rows, whichever is passed in by the parent component
  const [rows, maxRowLengthWithoutScroll] = Array.isArray(data) ? [data, 8] : [_rows, 7];

  return Array.isArray(rows) && rows.length > maxRowLengthWithoutScroll ? height : "fit-content";
};

export const getContent = (dataTable, isTableView) => {
  try {
    const extraIds = [0];
    const extraLabels = [dataTable.getColumnLabel(0)];
    const totalColumns = dataTable.getNumberOfColumns();
    const totalRows = dataTable.getNumberOfRows();
    let calculateTotals = false;

    for (let i = 0; i < totalColumns; i++) {
      // skip extraIds and extraLabel filling as backend sometimes adds role "data" to first column
      if (dataTable.getColumnRole(i) == "data" && i !== 0) {
        extraIds.push(i);
        let columnLabel = dataTable.getColumnLabel(i);
        extraLabels.push(columnLabel);

        // not every time chart needs total column
        if (columnLabel === "Total") {
          calculateTotals = !isTableView;
        }
      }
    }

    let data = [];
    let headers = extraLabels;

    for (let i = 0; i < totalRows; i++) {
      const row = [];
      let total = 0;

      for (let j = 0; j < extraIds.length; j++) {
        let cell = dataTable.getValue(i, extraIds[j]);
        let formattedCell = dataTable.getFormattedValue(i, extraIds[j]);

        //table can have rows as anchor tag strings
        let isLink = isTableView && formattedCell.includes("<a");

        // we manually calculate totals for chart view
        if (typeof cell == "number" && calculateTotals) {

          total += cell;
        }

        row.push(isLink ? cell : formattedCell);
      }

      if (calculateTotals) {
        row[row.length - 1] = numbro(total).format({ thousandSeparated: true, mantissa: 2 });
      }

      data.push(row);
    }

    return { data, headers };
  } catch (error) {
    console.error("Content error", error);
    return "";
  }
};

export const getOptions = (height, position, options = {}) => ({
  ...options,
  height,
  position,
  allowHtml: true,
  sort: "disable"
});
